import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Badge} from "primereact/badge";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {Message} from "primereact/message";
import {SelectButton} from "primereact/selectbutton";
import Avatar from "../../system/components/avatar";
import {useChat} from "../../models/chat/use-chat";
import {useChatMessage} from "../../models/chat-message/use-chat-message";
import {useChatParticipant} from "../../models/chat-participant/use-chat-participant";
import {usePermission} from "../../system/usePermission";
import {upsertWorkspaceChat} from "../../system/workspace-graph-slice";
import useUserEventLog from "../../models/user-event-log/use-user-event-log";
import {belongsToWorkspace, includesSearch, isActivityItem, isIncomingItem} from "../inbox/inbox-utils";
import {MobileEmpty, MobileError, MobileLoading, MobilePageHeader} from "./mobile-common";
import PushPromptBanner from "../../system/push/push-prompt-banner";
import {entityKey} from "./mobile-utils";

const sameEntity = (left, right) => String(entityKey(left)) === String(entityKey(right));

function otherMember(chat, currentUser) {
    return (chat?.users || []).find((membership) => String(membership?.member?.id) !== String(currentUser?.id))?.member;
}

function chatTitle(chat, currentUser, translate) {
    return chat?.type === "group"
        ? chat?.title || translate("mobile.messages.untitled-group")
        : otherMember(chat, currentUser)?.fullname || translate("mobile.messages.private-chat");
}

export default function MobileMessages({chatId, notificationMode = false, navigate, readOnly = false, translate}) {
    const chatApi = useChat();
    const participantApi = useChatParticipant();
    const eventApi = useUserEventLog();
    const permission = usePermission();
    const {user} = useSelector((state) => state.auth);
    const allNotifications = useSelector((state) => state.app.notifications || []);
    const activeWorkspace = useSelector((state) => state.app.workspaceMembershipData?.workspace);
    // only the active workspace's notifications (plus account-level ones)
    const globalNotifications = useMemo(
        () => allNotifications.filter((item) => belongsToWorkspace(item, activeWorkspace)),
        [activeWorkspace, allNotifications],
    );
    const workspaceMembership = useSelector((state) => state.app.workspaceMembershipData);
    const {chats, chatParticipants, inboxItems} = useSelector((state) => state.workspaceGraph);
    const workspaceKey = useSelector((state) => entityKey(state.app.workspaceMembershipData?.workspace));
    // «اعلان‌ها» section: notifications first, like the desktop page (task 12)
    const [view, setView] = useState("incoming");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [createType, setCreateType] = useState("private");
    const [groupTitle, setGroupTitle] = useState("");
    const [groupMembers, setGroupMembers] = useState([]);
    const [creating, setCreating] = useState(false);
    const [search, setSearch] = useState("");

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (notificationMode) await eventApi.getUserEventLogs();
            else await Promise.all([chatApi.getChats(), participantApi.getChatParticipants(), eventApi.getInboxItems()]);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoading(false);
        }
        // API hooks intentionally expose unstable method identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceKey]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    if (chatId) return <MobileChatDetail chatId={chatId} navigate={navigate} readOnly={readOnly} translate={translate}/>;

    if (notificationMode) {
        return (
            <div className="flex flex-column gap-3">
                <MobilePageHeader
                    title={translate("mobile.notifications.title")}
                    description={translate("mobile.notifications.description")}
                    action={<Button type="button" rounded text icon="pi pi-refresh" className="w-3rem h-3rem" loading={loading} onClick={refresh} aria-label={translate("mobile.common.retry")}/>} 
                />
                {error ? <MobileError message={translate("mobile.common.load-error")} retryLabel={translate("mobile.common.retry")} onRetry={refresh}/>
                    : loading && !globalNotifications.length ? <MobileLoading/>
                        : <EventList items={globalNotifications} eventApi={eventApi} navigate={navigate} translate={translate}/>} 
            </div>
        );
    }

    const conversationUnread = chatParticipants.reduce((total, item) => total + Number(item?.unreadCount || 0), 0);
    const incomingUnread = inboxItems.filter((item) => isIncomingItem(item) && !item.isRead).length;
    const activityUnread = inboxItems.filter((item) => isActivityItem(item) && !item.isRead).length;
    const options = [
        {label: `${translate("mobile.messages.incoming")} ${incomingUnread ? `(${incomingUnread})` : ""}`, value: "incoming"},
        {label: `${translate("mobile.messages.conversations")} ${conversationUnread ? `(${conversationUnread})` : ""}`, value: "conversations"},
        {label: `${translate("mobile.messages.activity")} ${activityUnread ? `(${activityUnread})` : ""}`, value: "activity"},
    ];
    const searchNeedle = search.trim().toLocaleLowerCase("fa");
    const visibleChats = chats.filter((chat) => !searchNeedle || [
        chatTitle(chat, user, translate),
        chat?.last_chat_message?.messageContent,
        ...(chat?.users || []).map((membership) => membership?.member?.fullname),
    ].some((value) => String(value || "").toLocaleLowerCase("fa").includes(searchNeedle)));
    const visibleEvents = inboxItems
        .filter(view === "activity" ? isActivityItem : isIncomingItem)
        .filter((item) => includesSearch(item, search));
    const availableMembers = (workspaceMembership?.workspace?.workspace_memberships || [])
        .filter((membership) => membership?.member?.id && String(membership.member.id) !== String(user?.id));
    const canCreateGroup = !readOnly && permission.can("create-chat-group");
    const createConversation = async () => {
        const selectedMemberships = createType === "group" ? groupMembers : [selectedMember].filter(Boolean);
        if (creating || readOnly || !selectedMemberships.length || (createType === "group" && (!canCreateGroup || !groupTitle.trim()))) return;
        setCreating(true);
        setError(null);
        try {
            const existing = createType === "private" && chats.find((chat) => chat.type === "private" && (chat.users || []).some((item) => String(item?.member?.id) === String(selectedMember.member.id)));
            const chat = existing || await chatApi.createChat({
                type: createType,
                ...(createType === "group" ? {title: groupTitle.trim()} : {}),
                users: {connect: selectedMemberships.map((membership) => ({documentId: membership.documentId}))},
            });
            if (!chat) throw new Error("chat-create-failed");
            setSelectedMember(null);
            setGroupMembers([]);
            setGroupTitle("");
            setCreateVisible(false);
            navigate(`/mobile/messages/${entityKey(chat)}`);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="flex flex-column gap-3">
            <MobilePageHeader
                title={translate("mobile.messages.title")}
                description={translate("mobile.messages.description")}
                action={<span className="flex gap-1">{!readOnly && <Button type="button" text className="text-xs gap-2" icon="pi pi-plus" label={translate("mobile.messages.new-conversation")} onClick={() => setCreateVisible((current) => !current)}/>}<Button type="button" rounded text icon="pi pi-refresh" className="w-3rem h-3rem" loading={loading} onClick={refresh} aria-label={translate("mobile.common.retry")}/></span>}
            />
            {!readOnly && <PushPromptBanner/>}
            {createVisible && <section className="surface-card border-1 border-primary-100 mobile-brand-radius p-3 mobile-brand-shadow flex flex-column gap-2">
                {canCreateGroup && <SelectButton className="flex w-full" value={createType} options={[{value: "private", label: translate("mobile.messages.private-chat")}, {value: "group", label: translate("mobile.messages.group-chat") }]} optionLabel="label" optionValue="value" allowEmpty={false} onChange={(event) => event.value && setCreateType(event.value)} pt={{button: {className: "flex-1 justify-content-center text-xs"}}}/>} 
                {createType === "group" ? <><InputText className="w-full min-h-3rem text-xs" value={groupTitle} placeholder={translate("mobile.messages.group-name")} onChange={(event) => setGroupTitle(event.target.value)}/><MultiSelect className="w-full text-xs shabnam-font" panelClassName="rtl shabnam-font" value={groupMembers} options={availableMembers} optionLabel="member.fullname" filter filterBy="member.fullname" display="chip" placeholder={translate("mobile.messages.select-members")} onChange={(event) => setGroupMembers(event.value)}/></> : <Dropdown className="w-full text-xs shabnam-font" panelClassName="rtl shabnam-font" value={selectedMember} options={availableMembers} optionLabel="member.fullname" filter filterBy="member.fullname" placeholder={translate("mobile.messages.select-member")} onChange={(event) => setSelectedMember(event.value)}/>} 
                <Button type="button" className="w-full text-xs justify-content-center" label={translate("mobile.messages.start-conversation")} loading={creating} disabled={creating || (createType === "group" ? !groupTitle.trim() || !groupMembers.length : !selectedMember)} onClick={createConversation}/>
            </section>}
            <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-search"/>
                <InputText className="w-full h-2rem text-xs text-right surface-card border-1 border-200 mobile-brand-radius shadow-none" value={search} placeholder={translate("mobile.common.search")} onChange={(event) => setSearch(event.target.value)}/>
            </IconField>
            <div className="w-full flex align-items-center gap-1 surface-100 mobile-brand-radius p-1" role="group">
                {options.map((option) => {
                    const active = view === option.value;
                    return <button key={option.value} type="button" aria-pressed={active} className={`flex-1 min-w-0 h-2rem flex align-items-center justify-content-center border-none mobile-brand-radius text-2xs cursor-pointer ${active ? "surface-card text-900 mobile-brand-shadow font-bold" : "surface-100 text-500"}`} onClick={() => setView(option.value)}>{option.label}</button>;
                })}
            </div>
            {error ? <MobileError message={translate("mobile.common.load-error")} retryLabel={translate("mobile.common.retry")} onRetry={refresh}/>
                : loading && !chats.length && !inboxItems.length ? <MobileLoading/>
                    : view === "conversations" ? <ConversationList chats={visibleChats} chatParticipants={chatParticipants} currentMembership={workspaceMembership} currentUser={user} navigate={navigate} translate={translate}/>
                        : <EventList items={visibleEvents} eventApi={eventApi} navigate={navigate} translate={translate}/>} 
        </div>
    );
}

function ConversationList({chats, chatParticipants, currentMembership, currentUser, navigate, translate}) {
    if (!chats.length) return <MobileEmpty icon="pi pi-comments" title={translate("mobile.messages.empty")} description={translate("mobile.messages.empty-description")}/>;
    return <div className="surface-card border-1 border-100 mobile-brand-radius overflow-hidden">{chats.map((chat) => {
        const member = otherMember(chat, currentUser);
        const participant = chatParticipants.find((item) => sameEntity(item?.chat, chat) && String(entityKey(item?.user)) === String(entityKey(currentMembership)));
        return (
            <button key={entityKey(chat)} type="button" className="w-full flex align-items-center gap-3 text-right surface-card border-none border-bottom-1 border-100 p-3 cursor-pointer" onClick={() => navigate(`/mobile/messages/${entityKey(chat)}`)}>
                {member ? <Avatar memberData={member} size="large"/> : <span className="w-3rem h-3rem flex align-items-center justify-content-center border-circle bg-primary-50 text-primary"><i className="pi pi-users"/></span>}
                <span className="flex-1 min-w-0">
                    <span className="flex align-items-center justify-content-between gap-2 mb-1"><strong className="text-xs text-900 text-overflow-ellipsis overflow-hidden white-space-nowrap">{chatTitle(chat, currentUser, translate)}</strong>{chat.lastMessageAt && <small className="text-3xs text-500 white-space-nowrap">{formatMobileTime(chat.lastMessageAt)}</small>}</span>
                    <span className="flex align-items-center gap-2"><small className="flex-1 block text-500 text-overflow-ellipsis overflow-hidden white-space-nowrap">{chat.last_chat_message?.messageContent || translate("mobile.messages.no-message")}</small>{participant?.unreadCount > 0 && <Badge value={participant.unreadCount} severity="danger"/>}</span>
                </span>
            </button>
        );
    })}</div>;
}

function EventList({items, eventApi, navigate, translate}) {
    const openEvent = async (item) => {
        if (!item.isRead) await eventApi.updateUserEventLog(entityKey(item), {isRead: true});
        const reference = item?.linkedEntity || item?.entity || item?.audit_log?.after;
        const referenceId = reference?.documentId || item?.entityDocumentId;
        const category = item?.eventCategory || item?.category;
        if (referenceId && ["task", "project", "goal", "team"].includes(category)) {
            navigate(`/mobile/${category === "task" ? "tasks" : `${category}s`}/${referenceId}`);
        }
    };
    if (!items.length) return <MobileEmpty icon="pi pi-bell" title={translate("mobile.notifications.empty")} description={translate("mobile.notifications.empty-description")}/>;
    return <div className="flex flex-column gap-2">{items.map((item) => (
        <button key={entityKey(item)} type="button" className={`w-full flex gap-3 text-right surface-card border-1 mobile-brand-radius p-3 cursor-pointer ${item.isRead ? "border-100" : "border-primary-200 mobile-brand-shadow"}`} onClick={() => openEvent(item)}>
            <span className="w-2rem h-2rem flex align-items-center justify-content-center mobile-brand-radius bg-primary-50 text-primary flex-shrink-0"><i className="pi pi-bell"/></span>
            <span className="flex-1 min-w-0"><strong className="block text-xs text-900 mb-1">{translate(item.title || "mobile.notifications.item")}</strong><small className="block text-500 line-height-3">{translate(item.message || item.contextLabel || "")}</small></span>
            {!item.isRead && <span className="w-1rem h-1rem border-circle bg-primary flex-shrink-0"/>}
        </button>
    ))}</div>;
}

function MobileChatDetail({chatId, navigate, readOnly, translate}) {
    const dispatch = useDispatch();
    const chatApi = useChat();
    const messageApi = useChatMessage();
    const {user} = useSelector((state) => state.auth);
    const {chats, chatMessages} = useSelector((state) => state.workspaceGraph);
    const [chat, setChat] = useState(() => chats.find((item) => String(entityKey(item)) === String(chatId)));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const listRef = useRef(null);

    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const selected = chat || await chatApi.getChat(chatId);
                if (!selected) throw new Error("chat-not-found");
                if (active) setChat(selected);
                await Promise.all([messageApi.getChatMessages(selected), chatApi.markChatRead(entityKey(selected))]);
            } catch (requestError) {
                if (active) setError(requestError);
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId]);

    const messages = useMemo(() => chatMessages
        .filter((item) => sameEntity(item.chat, chat))
        .sort((left, right) => new Date(left.sentAt || left.createdAt) - new Date(right.sentAt || right.createdAt)), [chat, chatMessages]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages.length]);

    const send = async (event) => {
        event.preventDefault();
        const content = text.trim();
        if (!content || !chat || sending || readOnly) return;
        setSending(true);
        setError(null);
        try {
            const created = await messageApi.createChatMessage({
                chat: entityKey(chat),
                messageContent: content,
                mentions: [],
                linkedEntity: [],
                clientNonce: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            });
            if (!created) throw new Error("message-create-failed");
            dispatch(upsertWorkspaceChat({...chat, last_chat_message: created, lastMessageAt: created.sentAt || created.createdAt || new Date().toISOString()}));
            setText("");
        } catch (requestError) {
            setError(requestError);
        } finally {
            setSending(false);
        }
    };

    if (loading && !chat) return <MobileLoading/>;
    return (
        <div className="h-full min-h-0 flex flex-column gap-2">
            <MobilePageHeader title={chat ? chatTitle(chat, user, translate) : translate("mobile.messages.chat")} description={translate(chat?.type === "group" ? "mobile.messages.group-chat" : "mobile.messages.chat-description")} onBack={() => navigate(-1)} action={chat && (otherMember(chat, user) ? <Avatar memberData={otherMember(chat, user)} size="large"/> : <span className="w-3rem h-3rem flex align-items-center justify-content-center border-circle bg-primary-50 text-primary"><i className="pi pi-users"/></span>)}/>
            {error && <Message className="text-xs" severity="error" text={translate("mobile.common.load-error")}/>} 
            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto flex flex-column gap-2 py-2">
                {messages.length ? messages.map((message) => {
                    const mine = String(message?.user?.member?.id || message?.user?.id) === String(user?.id);
                    return <div key={entityKey(message)} className={`max-w-80 mobile-brand-radius p-3 text-xs line-height-3 ${mine ? "align-self-start bg-primary-50 text-900" : "align-self-end surface-card border-1 border-100"}`}>{message.messageContent}<small className="flex justify-content-between gap-3 text-500 mt-1"><span>{message.user?.member?.fullname || message.user?.fullname || ""}</span><span className="ltr">{formatMobileTime(message.sentAt || message.createdAt)}</span></small></div>;
                }) : !loading && <MobileEmpty icon="pi pi-comments" title={translate("mobile.messages.no-message")} description={translate("mobile.messages.start-chat")}/>} 
            </div>
            {!readOnly && <form className="safe-area-bottom flex gap-2 surface-card mobile-brand-radius p-2 mobile-brand-shadow" onSubmit={send}>
                <InputText className="flex-1 min-w-0 border-none shadow-none text-xs" value={text} disabled={sending} onChange={(event) => setText(event.target.value)} placeholder={translate("mobile.messages.composer-placeholder")} aria-label={translate("mobile.messages.composer-placeholder")}/>
                <Button type="submit" rounded className="w-3rem h-3rem " icon="pi pi-send" loading={sending} disabled={!text.trim() || sending} aria-label={translate("send")}/>
            </form>}
        </div>
    );
}

function formatMobileTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("fa-IR", {hour: "2-digit", minute: "2-digit"}).format(date);
}
